/**
 * src/components/agent/VoiceModeOverlay.jsx — Full-screen Voice Mode call UI.
 *
 * Phase 82-05 — Chat System Unification (wave 4).
 *
 * Mounts a full-screen portal that wraps useVoiceModePresentation (which itself
 * wraps the Phase 90 useVoiceConversation orchestrator). Renders:
 *   - Top bar: back chevron, title, driving-mode toggle
 *   - Transcript scrollback (slides above orb)
 *   - Animated orb + karaoke caption
 *   - Bottom bar: voice name, language, mute, pause, end-call
 *   - Confirmation pill strip (Phase 76-02) when pending
 *   - Error strip when the backend surfaces an error
 *
 * Hooks at top of body (Phase 90-07 rule).
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, Mic, MicOff, Pause, Play, PhoneOff, SpellCheck } from 'lucide-react';
import useVoiceModePresentation from '../../lib/hooks/useVoiceModePresentation';
import { useT } from '../../lib/i18n/I18nContext';
import { useAgentMode } from '../../lib/agent/agentModeContext.jsx';
import VoiceOrb from './VoiceOrb';
import { useHoldToTalk } from '../../lib/voice/useHoldToTalk';
import TurnList from './TurnList';
import DrivingModeToggle from './DrivingModeToggle';
import VoicePickerSheet from './VoicePickerSheet';
import LanguagePickerSheet from './LanguagePickerSheet';
import FixTermSheet from './FixTermSheet';
import {
  overlayRootStyle,
  topBarStyle,
  topBarTitleStyle,
  chromeButtonStyle,
  orbAreaStyle,
  bottomBarStyle,
  bottomClusterStyle,
  controlBtnStyle,
  endBtnStyle,
  nameBtnStyle,
  errorStripStyle,
  pendingPillStyle,
  transcriptAreaStyle,
} from './VoiceModeOverlay.styles';

export default function VoiceModeOverlay({
  open,
  onClose,
  conversationId,
  // 90-07 AC-1 — AgentShell passes these resolved from chat.scope so the
  // voice pipeline is not scope-blind.
  contextProjectId = null,
  contextDealId = null,
  // 2026-04-21 persistence fix — when voice creates a new conversation
  // (opened with conversationId=null), callback fires with the new cid
  // so AgentShell can setCurrentId(cid). Prevents "turns wiped" on close.
  onConversationPersisted,
  // TICKET-912 slice 3: Quote Copilot voice, and a layer override so the overlay can
  // sit above the quote builder (zIndex 10000) when the bot was opened from it.
  copilot = null,
  zIndex = null,
}) {
  const { t } = useT();
  const presentation = useVoiceModePresentation({
    conversationId,
    contextProjectId,
    contextDealId,
    onConversationPersisted,
    copilot,
  });
  const { enterMode, exitMode } = useAgentMode();
  const [voiceSheetOpen, setVoiceSheetOpen] = useState(false);
  const [langSheetOpen, setLangSheetOpen] = useState(false);

  // F3 (2026-04-21) — enter 'conversation' mode on mount, exit on
  // unmount. This is what unlocks useStreamingTTS.speak() (the gate
  // checks agentMode === 'conversation'). Paired with F1's conditional
  // mount, mode goes idle → conversation → idle cleanly across open/close.
  useEffect(() => {
    enterMode('conversation');
    return () => exitMode('conversation');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- context setters
    // are stable; including them would loop on every AgentModeProvider render.
  }, []);
  // 2026-04-20 (session 5) — "Fix term" sheet for inline CEO-trusted
  // canonical correction. Prefilled from the most recent user transcript.
  const [fixSheetOpen, setFixSheetOpen] = useState(false);
  // 2026-04-20 (session 5) — auto-scroll ref for the bounded transcript area.
  // The container is now maxHeight:40vh + overflowY:auto; without this ref,
  // the CEO would have to manually scroll to follow the latest reply.
  const transcriptScrollRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setVoiceSheetOpen(false);
      setLangSheetOpen(false);
      setFixSheetOpen(false);
    }
  }, [open]);

  // 2026-04-21 F1 — AgentShell now conditionally mounts this component,
  // so `open` is always true while this body runs. The prior `if (!open)`
  // branch is dead and is replaced by an unmount-cleanup that fires
  // endSession when React tears the component down. This is the canonical
  // "close via ANY path" teardown: back button, Escape, voiceModeOpen
  // flipping false in AgentShell — all unmount, all run endSession.
  //
  // Prior bug (2026-04-20) this guards against: "I closed it and typed
  // and clicked on the microphone and on the background the whole thing
  // started making noises." Also covers the 2026-04-21 dictation-ghost
  // repro where the always-mounted VAD fired a conversation turn on top
  // of useDictation.
  useEffect(() => {
    return () => {
      try { presentation.controls?.endSession?.(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount-only
    // cleanup; including presentation.controls would re-fire endSession
    // on every render which would break in-flight turns.
  }, []);

  // 2026-04-20 (third pass) — auto-arm REMOVED. The second pass called
  // startTurn() on open, which transitioned the state machine straight to
  // 'recording'. That skipped the idle-VAD (which is designed to listen
  // for speech start before arming) and dropped us into recording-silence-
  // end VAD — which then sat forever waiting for 1.2s of pure silence on
  // a phone with ambient noise, producing the "Listening…" stuck state
  // seen at 07:54. Now we trust useVoiceAutoMode's idle-VAD to promote
  // the session when the user actually speaks. The orb is a visual-only
  // indicator; taps are still accepted as a manual fallback if the idle
  // VAD doesn't arm (iOS permission edge case).
  const autoArmRef = useRef(false);

  const { phase, orbMode, voiceChoice, language, drivingMode, isMuted, isPaused, controls, raw } = presentation;

  // TICKET-097 — press-and-hold orb = deterministic barge-in. Hold ≥250ms:
  // hard-stop TTS + record for as long as held (no VAD auto-end); release:
  // stop + process. A quick tap keeps the legacy idle tap-to-start below.
  const holdToTalk = useHoldToTalk({
    onHoldStart: () => controls.holdToTalkStart?.(),
    onHoldEnd: () => controls.holdToTalkEnd?.(),
  });
  // Phase 082 C3 — typed channels from the presentation hook. Defaults cover
  // older presentation mocks in tests that only supply { phase, raw, ... }.
  const finalTurns = presentation.finalTurns || [];
  const partialTranscript = presentation.partialTranscript || '';
  const systemNotice = presentation.systemNotice || null;
  const driving = !!drivingMode;

  // 2026-04-20 (session 5) — "Fix term" sheet prefills from the most recent
  // user bubble so tapping Fix right after a misheard turn drops that word
  // straight into the "Heard" input.
  const lastHeardText = useMemo(() => {
    if (!Array.isArray(finalTurns) || finalTurns.length === 0) return '';
    for (let i = finalTurns.length - 1; i >= 0; i--) {
      const t = finalTurns[i];
      if (t?.role === 'user') {
        return String(t.content || t.text || '').slice(0, 200);
      }
    }
    return '';
  }, [finalTurns]);

  // 2026-04-20 (session 5) — scroll-to-bottom on new turn or partial growth.
  // Kept above the early-return so hooks fire in a stable order per render.
  useEffect(() => {
    if (!open) return;
    const el = transcriptScrollRef.current;
    if (!el) return;
    // Use requestAnimationFrame so the DOM has painted the new bubble before
    // we compute scrollHeight; without it the scroll lands on the PREVIOUS
    // height and looks like it's one turn behind.
    const raf = requestAnimationFrame(() => {
      try { el.scrollTop = el.scrollHeight; } catch { /* noop */ }
    });
    return () => cancelAnimationFrame(raf);
  }, [open, finalTurns.length, partialTranscript]);

  if (!open) return null;

  const handleEnd = () => {
    controls.endSession?.();
    onClose?.();
  };

  // Typed channels only — never render raw.transcript (Whisper hallucinations).
  // finalTurns is promoted by useVoiceModePresentation when each turn completes.
  // Before the first turn completes, the transcript area is intentionally empty.
  const useTurnList = finalTurns.length > 0;

  const body = (
    <div
      style={zIndex ? { ...overlayRootStyle(driving), zIndex } : overlayRootStyle(driving)}
      data-testid="voice-mode-overlay"
      data-phase={phase}
      data-driving-mode={driving ? 'on' : 'off'}
      role="dialog"
      aria-label="Voice mode"
    >
      <div style={topBarStyle(driving)}>
        <button
          type="button"
          onClick={onClose}
          style={chromeButtonStyle(driving)}
          aria-label={t('agentChat.closeVoiceMode')}
          data-testid="voice-mode-back"
        >
          <ChevronLeft size={20} />
        </button>
        <div style={topBarTitleStyle}>Voice Mode</div>
        <DrivingModeToggle on={driving} onToggle={controls.toggleDriving} />
      </div>

      {(systemNotice || raw.error) && (
        <div
          style={{ ...errorStripStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}
          data-testid="voice-system-notice"
          role="status"
        >
          <span>{systemNotice || raw.error}</span>
          {/* Phase 2 — Retry pill surfaces when the 2-minute thinking
              timeout fires. Replays the last user transcript via
              retryLastTurn (no re-recording). */}
          {controls.retryLastTurn && typeof (raw.error || systemNotice || '').toString === 'function' && /time.?d?.? ?out|timeout/i.test(String(raw.error || systemNotice || '')) && (
            <button
              type="button"
              onClick={() => controls.retryLastTurn?.()}
              style={{
                background: 'rgba(255,255,255,0.22)',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                minHeight: 44,
              }}
              data-testid="voice-mode-retry"
              aria-label={t('agentChat.retryLastVoice')}
            >
              Retry
            </button>
          )}
        </div>
      )}

      <div
        ref={transcriptScrollRef}
        style={transcriptAreaStyle}
        data-testid="voice-mode-transcript-scroll"
      >
        {useTurnList && (
          <TurnList
            turns={finalTurns}
            karaokeTurnId={presentation.karaoke?.turnId || null}
            karaokeWordIndex={presentation.karaoke?.activeWordIndex ?? -1}
          />
        )}
        {partialTranscript && (
          <div
            data-testid="voice-partial"
            style={{
              alignSelf: 'flex-end',
              marginTop: 6,
              padding: '6px 12px',
              borderRadius: 12,
              // 2026-06-07 readability pass (frontend-design): the live "karaoke"
              // partial was var(--tm) @ opacity 0.6 italic — too low-contrast to
              // read while streaming. Lift to secondary-text token at 0.9 and
              // match the 14px body size; italic stays as the interim signal.
              color: 'var(--t2)',
              fontStyle: 'italic',
              opacity: 0.9,
              fontSize: 14,
              maxWidth: '78%',
            }}
          >
            {partialTranscript}
          </div>
        )}
      </div>

      <div style={orbAreaStyle(driving)}>
        {/* 2026-04-20 — VAD-driven flow. On iOS Safari the initial
            getUserMedia must fire from a user gesture, otherwise the
            overlay opens but never records. The orb wrapper is a button
            that does ONE thing: on the first tap (idle state) it calls
            startTurn() synchronously with the gesture, which grants mic
            access and arms the VAD. After that the flow is hands-free:
            VAD onSpeechEnd → stopRecording → agent → TTS → idle VAD
            re-arms for the next turn. To end the whole session, user
            hits the End button in the bottom bar. No tap-to-stop —
            that was the 02:24 report ("can't click again to stop",
            user wanted pure VAD after the initial unlock). */}
        <button
          type="button"
          {...holdToTalk.handlers}
          onContextMenu={(e) => e.preventDefault()}
          onClick={() => {
            // TICKET-097 — a completed hold synthesizes a click on release;
            // swallow it so it doesn't double-fire startTurn.
            if (holdToTalk.consumeHoldClick()) return;
            // Only the first tap matters — once the session is live,
            // the VAD handles every transition. Subsequent taps are
            // no-ops rather than duplicating startTurn or racing with
            // the state machine.
            if (raw.state === 'idle') controls.startTurn?.();
          }}
          aria-label={
            holdToTalk.isHolding ? 'Recording — release to send' :
            raw.state === 'idle' ? 'Tap to start listening' :
            raw.state === 'recording' ? 'Listening' :
            phase === 'thinking' ? 'Thinking' :
            phase === 'speaking' ? 'Speaking' :
            'Voice orb'
          }
          data-testid="voice-mode-orb-tap"
          data-phase={phase}
          data-holding={holdToTalk.isHolding ? 'true' : 'false'}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            margin: 0,
            cursor: raw.state === 'idle' ? 'pointer' : 'default',
            // TICKET-097 — iOS: keep long-press from triggering scroll,
            // text-selection magnifier, or the callout menu while holding.
            touchAction: 'none',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            userSelect: 'none',
          }}
        >
          <VoiceOrb
            phase={phase}
            orbMode={orbMode}
            audioLevel={phase === 'speaking' ? 0.6 : 0.3}
            drivingMode={driving}
            onModeCycle={controls.cycleOrbMode}
          />
        </button>
        <div
          data-testid="voice-mode-hint"
          style={{
            marginTop: 14,
            fontSize: 14,
            color: 'var(--tm)',
            textAlign: 'center',
            minHeight: 20,
            userSelect: 'none',
          }}
        >
          {holdToTalk.isHolding
            ? 'Recording — release to send'
            : raw.state === 'recording'
            ? 'Listening…'
            // 2026-04-21 — CEO report 22:52: "after I pause and before the
            // agent starts talking, the ball still says 'Speak whenever
            // you're ready'". Root cause: VAD ends the turn, state settles
            // back toward idle, but Whisper finalize takes ~8s (see app_
            // error_logs "Function exceeded 8s: voice.finalize_recording").
            // During that window raw.transcribing is true. Show 'Thinking…'
            // BEFORE the idle branch so user knows work is in flight.
            : raw.transcribing
              ? 'Thinking…'
              : phase === 'thinking'
                ? 'Thinking…'
                : phase === 'speaking'
                  ? 'Speaking…'
                  : raw.state === 'idle'
                    // idle-VAD is armed in the background; user just speaks.
                    // The orb is tap-able as a manual fallback if VAD didn't
                    // arm (rare iOS permission edge case).
                    ? 'Speak whenever you\'re ready'
                    : ''}
        </div>
        {raw.pendingProposal && (
          <div style={pendingPillStyle} data-testid="voice-mode-pending-pill">
            {/* 2026-04-21 — CEO report: pill said "Confirm: learn_correction"
                which is opaque. The server already ships a human-readable
                `summary` ("learn that X means Y") on the tool_proposal
                SSE event (voiceSseHandler.js:119). Prefer summary; fall
                back to tool name only when summary is missing. */}
            <span>
              {raw.pendingProposal.summary
                ? `Confirm: ${raw.pendingProposal.summary}`
                : `Confirm: ${raw.pendingProposal.name || 'proposal'}`}
            </span>
            <button
              type="button"
              style={controlBtnStyle(driving, true)}
              onClick={() => raw.confirmProposal?.(raw.pendingProposal.proposal_id)}
              data-testid="voice-mode-confirm"
              aria-label="Confirm proposal"
            >
              Yes
            </button>
            <button
              type="button"
              style={controlBtnStyle(driving, false)}
              onClick={() => raw.cancelProposal?.(raw.pendingProposal.proposal_id)}
              data-testid="voice-mode-cancel"
              aria-label="Cancel proposal"
            >
              No
            </button>
          </div>
        )}
      </div>

      <div style={bottomBarStyle(driving)}>
        <div style={bottomClusterStyle}>
          <button
            type="button"
            onClick={() => setVoiceSheetOpen(true)}
            style={nameBtnStyle(driving)}
            aria-label={`Voice: ${voiceChoice.displayName}`}
            data-testid="voice-mode-voice-name"
          >
            {voiceChoice.displayName}
          </button>
          <button
            type="button"
            onClick={() => setLangSheetOpen(true)}
            style={nameBtnStyle(driving)}
            aria-label={`Language: ${language.displayName}`}
            data-testid="voice-mode-language"
          >
            {language.id.toUpperCase()}
          </button>
        </div>

        <div style={bottomClusterStyle}>
          <button
            type="button"
            onClick={() => setFixSheetOpen(true)}
            style={controlBtnStyle(driving, false)}
            aria-label={t('agentChat.fixTerm')}
            data-testid="voice-mode-fix-term"
            title={t('agentChat.fixTerm')}
          >
            <SpellCheck size={18} />
          </button>
          <button
            type="button"
            onClick={controls.toggleMute}
            style={controlBtnStyle(driving, isMuted)}
            aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
            aria-pressed={isMuted}
            data-testid="voice-mode-mute"
          >
            {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
          <button
            type="button"
            onClick={controls.togglePause}
            style={controlBtnStyle(driving, isPaused)}
            aria-label={isPaused ? 'Resume' : 'Pause'}
            aria-pressed={isPaused}
            data-testid="voice-mode-pause"
          >
            {isPaused ? <Play size={18} /> : <Pause size={18} />}
          </button>
          <button
            type="button"
            onClick={handleEnd}
            style={endBtnStyle(driving)}
            aria-label="End call"
            data-testid="voice-mode-end"
          >
            <PhoneOff size={16} />
            <span>End</span>
          </button>
        </div>
      </div>

      <VoicePickerSheet
        open={voiceSheetOpen}
        currentVoiceId={voiceChoice.id}
        onSelect={controls.setVoice}
        onClose={() => setVoiceSheetOpen(false)}
      />
      <LanguagePickerSheet
        open={langSheetOpen}
        currentLanguageId={language.id}
        onSelect={controls.setLanguage}
        onClose={() => setLangSheetOpen(false)}
      />
      <FixTermSheet
        open={fixSheetOpen}
        initialHeard={lastHeardText}
        onClose={() => setFixSheetOpen(false)}
      />
    </div>
  );

  if (typeof document === 'undefined' || !document.body) return body;
  return createPortal(body, document.body);
}
